<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { throttle } from "lodash-es";
import { useBrightness, useResolvedColor } from "@/helpers/utility/Color";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ColorPickerPopout from "@core/ui/ColorPickerPopout.vue";

type Color = string | null;
interface Props {
  value: Color;
  extraColors?: Color[];
  popoutClass?: string;
  defaultValue?: NonNullable<Color>;
}
const props = withDefaults(defineProps<Props>(), {
  value: null,
  extraColors: () => [],
  popoutClass: "",
  defaultValue: "var(--color-text)"
});
const emit = defineEmits<{
  (e: "update:value", value: Color): void;
}>();

const button = ref<HTMLButtonElement>();

const safeColor = useResolvedColor(() => props.value ?? props.defaultValue);
const defaultBackgroundColor = useResolvedColor("var(--color-background-deep)");

const colorBrightness = useBrightness(safeColor);
const backgroundBrightness = useBrightness(defaultBackgroundColor);

const backgroundColor = computed(() => {
  const difference =
    1 - Math.abs(colorBrightness.value - backgroundBrightness.value);
  if (difference < 0.7) return "transparent";
  return `rgba(var(--tint-contrast), 1)`;
});

const throttledUpdateColor = throttle((color: Color) => {
  emit("update:value", color);
}, 500);

const colorPicker = reactive({
  open: false,
  x: 0,
  y: 0
});

function openColorPicker(e: MouseEvent) {
  colorPicker.open = true;
  if (button.value != null) {
    const buttonRect = button.value.getBoundingClientRect();
    colorPicker.x = buttonRect.left;
    colorPicker.y = buttonRect.bottom;
  } else {
    colorPicker.x = e.clientX;
    colorPicker.y = e.clientY;
  }
}
</script>

<template>
  <button
    ref="button"
    type="button"
    class="button-icon input-color-text"
    :title="$t('labels.textColor')"
    :style="{ backgroundColor: backgroundColor }"
    v-bind="$attrs"
    @click.prevent="openColorPicker"
  >
    <lucide-icon
      name="baseline"
      :style="{
        color: safeColor
      }"
    />
  </button>

  <color-picker-popout
    v-if="colorPicker.open"
    :value="value"
    :x="colorPicker.x"
    :y="colorPicker.y"
    :extra-colors="extraColors"
    :null-allowed="true"
    :transparent="false"
    :class="popoutClass"
    @update:value="throttledUpdateColor?.($event)"
    @close="colorPicker.open = false"
  />
</template>

<style lang="scss">
.input-color-text {
  border: 1px solid var(--color-border);

  &:active {
    border-color: var(--color-action);
  }
}
</style>
